import { useMemo, useState } from 'react';
import { useSentMessagesLogs } from './hooks';
import { MessageMedium } from 'constants/messageMediums';
import { PageHeader } from 'ui/pageHeader';
import { PAGE_SIZES, PaginationFooter } from 'ui/paginationFooter';
import { TriggeredMessagesRequest } from './types';
import { tableColumnsConfig } from './constants';
import { MessagesLogTable, TableToolbar } from './components';
import { DAY, getNewDate, getWeeksAgo } from 'utils/date';
import { useTablePaginationAndSorting } from './hooks/useTablePaginationAndSorting';
import { useCurrentProject } from 'api/ProjectsAPI';
import { Banner } from 'ui/banner';
import { BannerVariants } from 'ui/banner/types';
import { WarningFill16 } from 'ui/icons';
import { useGetInsightsMetrics } from './hooks/useGetInsightsMetrics';
import { FlexSpacer } from 'ui/flexSpacer';
import { InsightsSummary } from './components/insightsSummary';

const displayText = {
  title: 'Sent Messages',
  description: 'Log of messages sent through the API',
  bannerTitle: 'Failed to load data',
  bannerDescription: 'This project has been archived.',
};

export const CampaignsSentMessagesLogsPage = () => {
  const initialRequest = {
    page: 1,
    pageSize: 10,
    // Initial request with data until 7 days ago
    startDate: getWeeksAgo(getNewDate(), 1).startOf(DAY).toISOString(),
    endDate: getNewDate().endOf(DAY).toISOString(),
    orderBy: 'createdAt',
    sortDesc: true,
    messageMedium: MessageMedium.Email,
  };
  const [messageRequestParams, setMessageRequestParams] =
    useState<TriggeredMessagesRequest>(initialRequest);
  const { data: project, isLoading: isLoadingProject } = useCurrentProject();
  const isProjectArchived = project?.extendedProjectSettings?.isArchived;
  const { data, isLoading } = useSentMessagesLogs(messageRequestParams, {
    enabled: isProjectArchived === false && !!messageRequestParams,
  });
  const { data: insightsMetrics } = useGetInsightsMetrics(messageRequestParams, {
    enabled: isProjectArchived === false && !!messageRequestParams,
  });
  const { handlePaginationUpdate } = useTablePaginationAndSorting({
    messageRequestParams,
    setMessageRequestParams,
  });
  const messages = useMemo(
    () => data?.results.map((msg) => ({ ...msg, id: msg.messageId })),
    [data?.results],
  );

  return (
    <>
      {isProjectArchived && (
        <Banner
          title={displayText.bannerTitle}
          Icon={<WarningFill16 />}
          variant={BannerVariants.WARNING}
        >
          {displayText.description}
        </Banner>
      )}
      <FlexSpacer justifyContent="space-between">
        <PageHeader pageSubSection={<p>{displayText.description}</p>}>
          {displayText.title}
        </PageHeader>
        {!!insightsMetrics && (
          <InsightsSummary
            totalSends={insightsMetrics.overallMetrics['Total Sent']?.value}
            totalUnique={insightsMetrics.mediumMetrics['Unique Email Sends']?.value}
          />
        )}
      </FlexSpacer>
      <TableToolbar
        setMessageRequestParams={setMessageRequestParams}
        messageRequestParams={messageRequestParams}
        campaigns={data?.campaigns}
      />
      <MessagesLogTable
        isProjectArchived={isProjectArchived}
        isLoading={isLoading || isLoadingProject}
        campaigns={data?.campaigns}
        messages={messages}
        itemsPerPage={messageRequestParams.pageSize}
        tableColumnsConfig={tableColumnsConfig}
        setMessageRequestParams={setMessageRequestParams}
        messageRequestParams={messageRequestParams}
      />
      <PaginationFooter
        currentPage={messageRequestParams.page}
        itemsPerPage={messageRequestParams.pageSize}
        numItems={data?.count}
        pageSizes={PAGE_SIZES}
        onUpdate={handlePaginationUpdate}
      />
    </>
  );
};
